/**
 * The field contract.
 *
 * Everything in this file is shared by three parties that must not disagree: the database
 * (which stores it), the API (which validates it) and the technician's phone (which renders
 * it offline, from its own copy, with no chance to ask). A word spelled differently in any
 * one of them is a bug nobody sees until a van is in a driveway.
 *
 * The organising rule of `FIELD.md`, restated because it decides what may live here:
 *
 *   **The app knows the mechanism. The business declares the vocabulary.**
 *
 * So a *kind* is here and a *label* is not. `equipment` is here because the client behaves on
 * it; *Pas le matériel adéquat* is a row a business owns, in its own French, and never a
 * string constant in this file.
 */

// ---------------------------------------------------------------------------
// The stop
// ---------------------------------------------------------------------------

/**
 * What the technician is doing, derived rather than stored.
 *
 * *En route*, *arrived* and *working* are **not statuses**. The visit is `in_progress` from
 * the moment the technician leaves, and which of the three it is follows from which
 * timestamps are set. That keeps one status vocabulary — the one the board and the API
 * already validate — and makes the timeline the record rather than a second opinion about it.
 */
export const STOP_STATES = [
  'scheduled',
  'en_route',
  'delayed',
  'arrived',
  'working',
  'done',
  'not_done',
  'cancelled',
] as const;
export type StopState = (typeof STOP_STATES)[number];

/** The timestamps a stop's state is read from. Nulls are the normal case. */
export type StopTimestamps = {
  status: string;
  enRouteAt?: string | null;
  etaAt?: string | null;
  arrivedAt?: string | null;
  departedAt?: string | null;
  completedAt?: string | null;
};

/**
 * Read the state of a stop.
 *
 * Order matters and is not arbitrary: a terminal status outranks any timestamp, because a
 * visit the office cancelled while the technician was in a basement is cancelled whatever
 * the phone recorded on the way there.
 */
export function stopState(visit: StopTimestamps): StopState {
  if (visit.status === 'cancelled') return 'cancelled';
  if (visit.status === 'not_done' || visit.status === 'no_show') return 'not_done';
  if (visit.status === 'completed' || visit.completedAt) return 'done';
  if (visit.arrivedAt) return visit.departedAt ? 'done' : 'working';
  if (visit.enRouteAt) return visit.etaAt ? 'delayed' : 'en_route';
  return 'scheduled';
}

/**
 * What the technician may do next.
 *
 * The client renders exactly these and nothing else — *the client must never render a
 * control the account cannot use*, and the same holds for a control the state cannot accept.
 * `cannot_do` is reachable from *en route* as well as from the door: a van that breaks down
 * fails the stop before anyone knocks.
 */
export const TRANSITIONS = [
  'en_route',
  'delay',
  'arrive',
  'start',
  'pause',
  'complete',
  'cannot_do',
  'undo',
] as const;
export type Transition = (typeof TRANSITIONS)[number];

export function allowedTransitions(state: StopState): Transition[] {
  switch (state) {
    case 'scheduled':
      return ['en_route', 'arrive', 'cannot_do'];
    case 'en_route':
      return ['delay', 'arrive', 'cannot_do', 'undo'];
    case 'delayed':
      return ['arrive', 'cannot_do', 'undo'];
    case 'arrived':
    case 'working':
      return ['complete', 'cannot_do'];
    case 'done':
    case 'not_done':
      // Undo, not re-open: a technician who tapped the wrong card gets one step back, and
      // anything beyond that is the office's to correct.
      return ['undo'];
    case 'cancelled':
      return [];
  }
}

/**
 * How a visit that ended in work turned out.
 *
 * *Done* is not one thing. `appointments.sequence` already expresses "second trip" and the
 * services differ per visit because the first diagnoses and the second installs the part
 * that had to be ordered — so the visit says which kind it turned out to be, and the job
 * stays the office's to move.
 */
export const APPOINTMENT_OUTCOMES = ['complete', 'partial', 'return_needed'] as const;
export type AppointmentOutcome = (typeof APPOINTMENT_OUTCOMES)[number];

// ---------------------------------------------------------------------------
// Why a visit fails
// ---------------------------------------------------------------------------

/**
 * The cross-tenant meaning of a business's own reason list.
 *
 * The same device that gave pipeline stages a `stage_kind`: the businesses keep their words —
 * *Client absent*, *Machine encore propre* — and reporting works across them without matching
 * French strings.
 *
 * `equipment` and `material` are deliberately apart. *Matériel* is ambiguous in French
 * between the part and the tool, and the two point at different fixes: knowing the access
 * requirements before loading the van, versus stocking the default parts kit.
 */
export const VISIT_REASON_KINDS = [
  'customer',
  'access',
  'travel',
  'equipment',
  'material',
  'safety',
  'technician',
  'not_needed',
  'other',
] as const;
export type VisitReasonKind = (typeof VISIT_REASON_KINDS)[number];

/**
 * The reason kinds that record a customer who was not there.
 *
 * `no_show` keeps its own status for this, because *Client absent* is the first configured
 * reason on all three tenants and it has a procedure — the wait, the photograph of the door,
 * the position fix — that no other reason has. Everything else lands in `not_done`.
 */
export const NO_SHOW_REASON_KINDS: readonly VisitReasonKind[] = ['customer'];

// ---------------------------------------------------------------------------
// Captures
// ---------------------------------------------------------------------------

/**
 * The capture kinds the app *behaves* on, and therefore code.
 *
 * Everything else — `unit_outdoor`, `roof`, `panel`, `meter` — is a row a business declares
 * with its own FR/EN labels. An air-conditioning job is two machines in two places, and a
 * photograph of the wrong one answers nothing; a plumber never sees either kind.
 *
 * `voice_note` and `prospect` are here for the same reason as the rest: the app does
 * something specific with them — transcription, and an observation of a place that is not
 * attached to a job at all.
 */
export const CORE_CAPTURE_KINDS = [
  'before',
  'after',
  'defect',
  'hazard',
  'nameplate',
  'access',
  'attempted_visit',
  'document',
  'signature',
  'voice_note',
  'prospect',
  'reading',
] as const;
export type CoreCaptureKind = (typeof CORE_CAPTURE_KINDS)[number];

const CORE_CAPTURE_SET: ReadonlySet<string> = new Set(CORE_CAPTURE_KINDS);
export const isCoreCaptureKind = (value: string): value is CoreCaptureKind => CORE_CAPTURE_SET.has(value);

/** What a service can require: the four shapes a definition of done is made of. */
export const CAPTURE_TYPES = ['photo', 'reading', 'confirmation', 'signature'] as const;
export type CaptureType = (typeof CAPTURE_TYPES)[number];

/**
 * What a signature is for.
 *
 * Three things are signed and they are not interchangeable. The invoice is **not** among
 * them: it is issued by the office, and the acknowledgement of it is the payment.
 */
export const SIGNATURE_PURPOSES = ['work_acceptance', 'quote_acceptance', 'safety_notice'] as const;
export type SignaturePurpose = (typeof SIGNATURE_PURPOSES)[number];

// ---------------------------------------------------------------------------
// Readings
// ---------------------------------------------------------------------------

/**
 * A reading is a number with a unit, taken somewhere, with the instrument photograph kept as
 * evidence. The number is the data; the photograph is the proof — the same relationship a
 * voice note has to its transcript.
 */
export const READING_PHASES = ['before', 'after', 'single'] as const;
export type ReadingPhase = (typeof READING_PHASES)[number];

/**
 * The derived values, as a small closed set of formulas in code chosen by configuration.
 *
 * A business declares *that* a service computes ΔT; it does not get to declare arithmetic.
 * Each takes its inputs by requirement `key`, which is why the keys are part of the
 * configuration and the formula is not.
 */
export const DERIVED_FORMULAS = {
  /** Supply minus return. The number an air-conditioning service is judged on. */
  delta_t: { inputs: ['supply', 'return'], unit: '°C' },
  /** Velocity through a register of known area: m/s × m² → m³/s, reported in L/s. */
  velocity_to_flow: { inputs: ['velocity', 'area_m2'], unit: 'L/s' },
  /** Exchanger balance: supply flow over exhaust flow, as a percentage. */
  exchanger_balance: { inputs: ['supply_flow', 'exhaust_flow'], unit: '%' },
} as const;
export type DerivedFormula = keyof typeof DERIVED_FORMULAS;

export function computeDerived(formula: DerivedFormula, inputs: Record<string, number>): number | null {
  const need = DERIVED_FORMULAS[formula].inputs;
  for (const key of need) if (typeof inputs[key] !== 'number' || Number.isNaN(inputs[key])) return null;

  switch (formula) {
    case 'delta_t':
      return round(inputs.supply! - inputs.return!, 2);
    case 'velocity_to_flow':
      // m/s × m² = m³/s; litres per second is the unit a balancing report is written in.
      return round(inputs.velocity! * inputs.area_m2! * 1000, 1);
    case 'exchanger_balance':
      return inputs.exhaust_flow === 0 ? null : round((inputs.supply_flow! / inputs.exhaust_flow!) * 100, 1);
  }
}

/** Outside its declared range. Raises the out-of-spec mark on the stop and in the report. */
export function outOfSpec(value: number, min: number | null, max: number | null): boolean {
  if (min !== null && value < min) return true;
  if (max !== null && value > max) return true;
  return false;
}

function round(value: number, places: number): number {
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
}

// ---------------------------------------------------------------------------
// Safety
// ---------------------------------------------------------------------------

/**
 * Who the hazard is a hazard to, which decides what the technician does.
 *
 * A hazard to the technician means *stop and leave* — the visit fails with reason kind
 * `safety`. A hazard to the customer or the premises means *document it*; the work may
 * continue and the unit may be left off.
 */
export const HAZARD_TARGETS = ['technician', 'customer'] as const;
export type HazardTarget = (typeof HAZARD_TARGETS)[number];

/**
 * What a severity *means*, across businesses. The labels and the wording of the notice are
 * a business's own, exactly like a reason.
 */
export const HAZARD_SEVERITY_KINDS = ['do_not_operate', 'fix_before_next_service', 'advisory'] as const;
export type HazardSeverityKind = (typeof HAZARD_SEVERITY_KINDS)[number];

/** What a hazard's outcome creates. A hazard is the best justification a quote will ever have. */
export const HAZARD_OUTCOMES = ['return_visit', 'quote_repair', 'refer_other_trade', 'no_further_action'] as const;
export type HazardOutcome = (typeof HAZARD_OUTCOMES)[number];

// ---------------------------------------------------------------------------
// Access
// ---------------------------------------------------------------------------

/**
 * What an access requirement implies, as flags the app acts on.
 *
 * The list itself — *ladder over 6 m*, *roof hatch key*, *elevator booking* — is the
 * business's. What each chip *causes* is code: equipment to load, extra time, a second
 * person, a cost line on the quote, a key-holder to ring.
 *
 * `height` and `confined_space` carry a second consequence: they are the lone-worker
 * trigger. One chip, two consequences.
 */
export const ACCESS_IMPLICATIONS = [
  'equipment',
  'extra_time',
  'second_person',
  'cost_line',
  'contact',
  'height',
  'confined_space',
] as const;
export type AccessImplication = (typeof ACCESS_IMPLICATIONS)[number];

// ---------------------------------------------------------------------------
// Position
// ---------------------------------------------------------------------------

/**
 * Position, and the two numbers that keep it honest.
 *
 * A fix worse than this is shown as *approximate*, never as a point on a map. A phone in a
 * mechanical room gets no fix at all, and the work still happened — self-reported *arrived*
 * remains the record of record.
 */
export const POSITION_APPROXIMATE_ABOVE_M = 100;

/**
 * How long a breadcrumb lives.
 *
 * Long enough to reconstruct a disputed day, not long enough to be a movement history. The
 * arrival fix is not a breadcrumb: it is part of the job record and stays with it.
 */
export const POSITION_TTL_SECONDS = 36 * 60 * 60;

/** How often the client reports while on shift. Coarse on purpose; precision costs battery. */
export const POSITION_INTERVAL_SECONDS = 180;

// ---------------------------------------------------------------------------
// The phone
// ---------------------------------------------------------------------------

/**
 * How long the client stays unlocked.
 *
 * Two weeks of customers' names, telephone numbers, addresses and **gate codes** sit in
 * IndexedDB on a device that lives in a van. Sessions are 180 days by design so a technician
 * in a basement never has to sign in; the re-lock is what makes that acceptable.
 */
export const FIELD_LOCK_AFTER_SECONDS = 15 * 60;

/** The long edge a captured photograph is downscaled to before it is stored. */
export const CAPTURE_MAX_EDGE_PX = 2048;

/** Chunk size for a resumable upload: small enough to finish on one bar of LTE. */
export const UPLOAD_CHUNK_BYTES = 256 * 1024;
